// The app's toast API. Shows react-hot-toast toasts (mounted once in App.js) and accepts the
// call shapes the old react-toastify code used, so toast.info / toast.warn / autoClose keep working.
import { isValidElement } from "react";
import hotToast from "react-hot-toast";
import { wasJustShown } from "../events/planLimitEvents";

const FALLBACK = "Something went wrong. Please try again.";

// Server errors sometimes arrive as objects or lists; React can't render those, so turn them into text.
const text = (message) => {
  if (typeof message === "string") return message.trim() ? message : FALLBACK;
  if (typeof message === "number" || typeof message === "function" || isValidElement(message)) return message;
  if (message instanceof Error) return message.message || FALLBACK;
  if (Array.isArray(message)) return message.map(text).filter((m) => typeof m === "string").join(" ") || FALLBACK;
  if (message && typeof message === "object") {
    const known = message.message || message.error || message.detail || message.msg;
    return known ? text(known) : text(Object.values(message).flat()[0]);
  }
  return FALLBACK;
};

const InfoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" fill="#3B82F6" />
    <path d="M12 11v6M12 7.5v.5" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const WarningIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M10.3 3.9 1.9 18a2 2 0 0 0 1.7 3h16.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" fill="#F59E0B" />
    <path d="M12 9v4.5M12 17v.5" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const options = (opts = {}) => {
  const { autoClose, toastId, id, duration, icon, style, className, ariaProps } = opts || {};
  const out = {};
  if (id || toastId) out.id = id || toastId;
  if (duration !== undefined) out.duration = duration;
  else if (autoClose === false) out.duration = Infinity;
  else if (typeof autoClose === "number") out.duration = autoClose;
  if (icon !== undefined) out.icon = icon;
  if (style) out.style = style;
  if (className) out.className = className;
  if (ariaProps) out.ariaProps = ariaProps;
  return out;
};

const toast = (message, opts) => hotToast(text(message), options(opts));
toast.success = (message, opts) => hotToast.success(text(message), options(opts));
toast.error = (message, opts) => (wasJustShown(message) ? undefined : hotToast.error(text(message), options(opts)));
toast.loading = (message, opts) => hotToast.loading(text(message), options(opts));
toast.info = (message, opts) => hotToast(text(message), { icon: <InfoIcon />, ...options(opts) });
toast.warn = (message, opts) => hotToast(text(message), { icon: <WarningIcon />, duration: 3500, ...options(opts) });
toast.warning = toast.warn;
toast.dismiss = (id) => hotToast.dismiss(id);

export { toast };
export default toast;
